import React from 'react';
import styled, { css } from 'styled-components';

import { ProjectContext } from '../../../../state/project-state/project-state';
import { idToPastelHex } from '../../../../utils/color';
import { reset } from '../../../../utils/styled-reset';
import { Icon } from '../../../icon/icon';
import { Stack } from '../../../stack/stack';

type TemporaryTagData = {
  name: string;
  bgColor: string;
};

type ProjectTagData = {
  id: string;
};

type TagData = TemporaryTagData | ProjectTagData;

type Props = {
  tag: TagData;
  onClose?: () => void | Promise<void>;
  disabled?: boolean;
};

const AssetTagChip: React.FC<Props> = ({ tag, disabled = false, onClose }) => {
  const { project } = React.useContext(ProjectContext);
  const tagName = React.useMemo(() => {
    if ('name' in tag && tag.name && tag.name.trim()) {
      return tag.name.trim();
    }
    if ('id' in tag && tag.id) {
      const foundTag = project?.assetTags?.find((t) => t.id === tag.id);
      return foundTag?.name ?? tag.id;
    }
  }, [project?.assetTags, tag]);

  const backgroundColor = React.useMemo(() => {
    if ('bgColor' in tag && tag.bgColor) {
      return tag.bgColor;
    }
    if ('id' in tag && tag.id) {
      return idToPastelHex(tag.id, 70, 55);
    }
    return '#e5e7eb';
  }, [tag]);

  return (
    <ChipContainer
      $backgroundColor={backgroundColor}
      $disabled={disabled}
      alignItems="center"
      direction="row"
      role="button"
      spacing={0.35}
    >
      <Text>{tagName}</Text>
      {onClose && (
        <DeleteIcon
          aria-label="Remove tag"
          data-chip-delete="true"
          icon={['fad', 'times-circle']}
          onClick={
            disabled
              ? undefined
              : (e) => {
                  e.stopPropagation();
                  void onClose?.();
                }
          }
        />
      )}
    </ChipContainer>
  );
};

type ChipContainerProps = { $disabled: boolean; $backgroundColor: string };

const ChipContainer = styled(Stack)<ChipContainerProps>`
  ${reset}

  width: fit-content;
  position: relative;
  padding: 0.3em;
  background: ${(p) => p.$backgroundColor};
  color: white;
  border: 1px solid var(--color-border, #d0d7de);
  border-radius: 0.4em;
  box-shadow: 0 0.1em 0.2em rgba(0, 0, 0, 0.16);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.1;

  ${(p) =>
    p.$disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `}
`;

const Text = styled.span`
  display: inline-block;
  max-width: 18rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteIcon = styled(Icon)`
  position: absolute;
  right: 0.25em;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  opacity: 0;
  color: #333;
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: 9999px;
  padding: 0.12em;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  transition:
    opacity 0.15s ease-in-out,
    background-color 0.15s ease-in-out,
    transform 0.1s ease-in-out;
  cursor: pointer;

  ${ChipContainer}:hover & {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.95);
  }

  &:active {
    transform: translateY(-50%) scale(0.96);
  }
`;

export { AssetTagChip };

// Utilities moved to ../../../../utils/color
